import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { User, Phone, KeyRound, Monitor, Smartphone } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { TeamMember } from "@/hooks/useTeam";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }
  return digits
    .replace(/^(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
}

interface TeamMemberEditDialogProps {
  member: TeamMember | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  getRoleOptions: () => { value: AppRole; label: string }[];
  onSuccess: () => void;
}

export function TeamMemberEditDialog({
  member,
  open,
  onOpenChange,
  getRoleOptions,
  onSuccess,
}: TeamMemberEditDialogProps) {
  const [role, setRole] = useState<AppRole>("cashier");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [operatorPin, setOperatorPin] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (member) {
      setRole(member.role);
      setFullName(member.profile?.full_name || "");
      setPhone(member.profile?.phone ? formatPhone(member.profile.phone) : "");
      setOperatorPin((member as any).operator_pin || "");
    }
  }, [member]);

  const handleSubmit = async () => {
    if (!member) return;

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("update-team-member", {
        body: {
          restaurantId: member.restaurant_id,
          memberUserId: member.user_id,
          restaurantUserId: member.id,
          fullName: fullName.trim() || null,
          phone: phone.replace(/\D/g, "") || null,
          role,
          operatorPin: operatorPin.trim() || null,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success("Funcionário atualizado com sucesso!");
      onSuccess();
    } catch (error: any) {
      console.error("Error updating member:", error);
      toast.error(error.message || "Erro ao atualizar funcionário");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar Funcionário</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {member?.email && (
            <div className="space-y-2">
              <Label>E-mail</Label>
              <p className="text-sm text-muted-foreground bg-muted/50 px-3 py-2 rounded-md">
                {member.email}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="fullName">Nome completo</Label>
            <div className="relative">
              <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Nome do funcionário"
                className="pl-9"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Telefone</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                placeholder="(00) 00000-0000"
                className="pl-9"
              />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Monitor className="h-4 w-4 text-primary" />
              Identificação — Terminal PDV (balcão)
            </div>
            <p className="text-xs text-muted-foreground">
              PIN numérico para identificação rápida no terminal compartilhado. Opcional.
            </p>
            <div className="space-y-2">
              <Label htmlFor="operatorPin">PIN do PDV (4-6 dígitos)</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="operatorPin"
                  value={operatorPin}
                  onChange={(e) => setOperatorPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="Ex: 1234"
                  className="pl-9"
                  inputMode="numeric"
                  maxLength={6}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Deixe vazio para não usar
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Cargo</Label>
            <Select value={role} onValueChange={(v) => setRole(v as AppRole)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o cargo" />
              </SelectTrigger>
              <SelectContent>
                {getRoleOptions().map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
